import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Check,
  Building2,
  AlertCircle,
  ExternalLink,
  Loader2,
} from 'lucide-react';

export interface FetchedLogoData {
  name: string;
  domain: string;
  logoUrl: string | null;
  websiteUrl: string;
}

export interface FetchedLogoPreviewCardProps {
  result: FetchedLogoData;
  onApply: () => void;
  onReset: () => void;
  isApplying?: boolean;
  applyLabel?: string;
  resetLabel?: string;
  className?: string;
}

export const FetchedLogoPreviewCard: React.FC<FetchedLogoPreviewCardProps> = ({
  result,
  onApply,
  onReset,
  isApplying = false,
  applyLabel = 'Apply as Organization Logo',
  resetLabel = 'Search Again',
  className = '',
}) => {
  return (
    <div
      className={`rounded-2xl border border-purple-200 bg-gradient-to-b from-purple-50/40 to-white p-4 space-y-4 animate-in fade-in zoom-in-95 duration-200 ${className}`}
    >
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/60">
          <Check className="w-3 h-3 text-emerald-600" />
          Logo Found
        </span>
        <a
          href={result.websiteUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] text-[#A655F8] hover:underline flex items-center gap-1"
        >
          <span>{result.domain}</span>
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative w-16 h-16 rounded-xl border border-slate-200 bg-white flex items-center justify-center overflow-hidden shrink-0 shadow-sm p-1.5">
          {result.logoUrl ? (
            <img
              src={result.logoUrl}
              alt={result.name}
              className="w-full h-full object-contain"
            />
          ) : (
            <Building2 className="w-8 h-8 text-slate-400" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-bold text-slate-900 truncate">
            {result.name}
          </h4>
          <p className="text-xs text-slate-500 mt-0.5 truncate">
            {result.logoUrl
              ? 'High-resolution official logo ready'
              : 'No image detected for this domain'}
          </p>
        </div>
      </div>

      {result.logoUrl ? (
        <div className="flex items-center gap-2 pt-1">
          <Button
            type="button"
            onClick={onApply}
            disabled={isApplying}
            className="flex-1 h-10 px-5 rounded-xl bg-[#A655F8] hover:bg-[#9333EA] text-white shadow-none font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isApplying ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Applying Logo...</span>
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                <span>{applyLabel}</span>
              </>
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onReset}
            disabled={isApplying}
            className="h-10 px-4 rounded-xl border-slate-200 text-slate-700 bg-white hover:bg-white hover:text-slate-700 shadow-none font-medium text-sm transition-none"
          >
            {resetLabel}
          </Button>
        </div>
      ) : (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">
            No logo could be extracted from this site. You can upload an image file directly from your device instead.
          </p>
        </div>
      )}
    </div>
  );
};
